import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Knowledge base for the chatbot
const knowledgeBase = {
  services: {
    keywords: ['service', 'services', 'what do you offer', 'what services', 'help with', 'need help'],
    response: 'We offer comprehensive tax and business services including:\n\n' +
      '📋 Tax Services:\n' +
      '• Tax Preparation (Personal & Business)\n' +
      '• Advance Tax Planning\n' +
      '• FBAR & FATCA Filing\n' +
      '• ITIN Assistance\n' +
      '• Federal & State Notice Resolution\n' +
      '• Tax Amendments\n' +
      '• Expert Consulting & Audit Defense\n\n' +
      '💼 Business Services:\n' +
      '• Business Consulting & Strategy\n' +
      '• Bookkeeping & Accounting\n' +
      '• Staffing Solutions\n\n' +
      'Would you like more details about any specific service?'
  },
  tax: {
    keywords: ['tax', 'tax filing', 'tax preparation', 'tax return', 'file taxes', 'tax help'],
    response: 'We provide expert tax preparation services for:\n\n' +
      '✅ Personal Tax Returns\n' +
      '✅ Business Tax Returns\n' +
      '✅ Expat Tax Filing\n' +
      '✅ Maximum Refund Guarantee\n' +
      '✅ Fast & Accurate Filing\n\n' +
      'You can start by uploading your documents in your dashboard. Need more information?'
  },
  business: {
    keywords: ['business', 'consulting', 'business consulting', 'bookkeeping', 'accounting', 'staffing'],
    response: 'Our business services include:\n\n' +
      '🎯 Business Consulting & Strategy\n' +
      '📊 Bookkeeping & Accounting\n' +
      '👥 Staffing Solutions (IT & Business)\n' +
      '💼 Process Automation\n' +
      '📈 Digital Transformation\n\n' +
      'We help businesses from startup to scale-up. Want to know more?'
  },
  contact: {
    keywords: ['contact', 'phone', 'email', 'address', 'location', 'reach', 'call', 'email address'],
    response: 'You can reach us at:\n\n' +
      '📍 Address: DALLAS, GA, 30132, USA\n' +
      '📧 Email: info@nexenconsults.com\n' +
      '📞 Phone: +91 996-696-9809, +1 (943) 678- 9878\n' +
      '🕐 Hours: Monday – Saturday: 24/7 Support, PST\n' +
      '         Sunday: By Appointment\n\n' +
      'Would you like to visit our contact page for more details?'
  },
  pricing: {
    keywords: ['price', 'pricing', 'cost', 'fee', 'how much', 'charge', 'rate'],
    response: 'Our pricing varies based on the service and complexity of your needs. For accurate pricing:\n\n' +
      '• Tax Preparation: Based on form complexity\n' +
      '• Business Services: Custom quotes available\n' +
      '• Consulting: Hourly or project-based\n\n' +
      'I recommend contacting our experts for a personalized quote. Would you like me to take you to our contact page?'
  },
  hours: {
    keywords: ['hours', 'open', 'available', 'when', 'time', 'working hours', 'office hours'],
    response: 'Our office hours:\n\n' +
      '🕐 Monday – Saturday: 24/7 Support, PST\n' +
      '🕐 Sunday: By Appointment\n\n' +
      'We\'re here to help whenever you need us!'
  },
  about: {
    keywords: ['about', 'who are you', 'company', 'nexen', 'what is nexen'],
    response: 'Nexen Tax Consults is a leading provider of tax and business consulting services. We specialize in:\n\n' +
      '✅ Expert tax preparation and filing\n' +
      '✅ Business consulting and strategy\n' +
      '✅ IRS audit defense\n' +
      '✅ International tax compliance\n' +
      '✅ Staffing solutions\n\n' +
      'Our team of certified professionals is dedicated to helping you achieve your financial goals.'
  },
  refund: {
    keywords: ['refund', 'refund status', 'tax refund', 'when', 'track refund'],
    response: 'To check your refund status:\n\n' +
      '1. Visit our "Fund Info" page from the main menu\n' +
      '2. Enter your tracking information\n' +
      '3. View your refund status\n\n' +
      'For detailed assistance, please contact our team.'
  },
  signup: {
    keywords: ['sign up', 'register', 'create account', 'account', 'signup', 'registration'],
    response: 'To create an account:\n\n' +
      '1. Click "Sign Up" in the top navigation\n' +
      '2. Fill in your details\n' +
      '3. Verify your email and mobile\n' +
      '4. Complete registration\n\n' +
      'Once registered, you can upload documents and track your tax returns!'
  },
  login: {
    keywords: ['login', 'sign in', 'log in', 'access account'],
    response: 'To log in:\n\n' +
      '1. Click "Sign In" in the top navigation\n' +
      '2. Enter your email and password\n' +
      '3. Access your dashboard\n\n' +
      'Need help accessing your account? Contact our support team.'
  }
};

// Default responses
const defaultResponses = [
  "I'm here to help! Ask me about our services, contact information, or anything else about Nexen Tax Consults.",
  "How can I assist you today? I can answer questions about our tax and business services.",
  "Feel free to ask me anything about Nexen Tax Consults. I'm here to help!"
];

const fallbackResponse = "I'm not sure about that specific question. Our expert team can help you with that! Would you like me to take you to our contact page where you can reach out to our specialists?";

export default function ChatAssistant() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text: "Hello! 👋 I'm Nexen's assistant. How can I help you today?",
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const scrollTriggeredRef = useRef(false);
  const userInteractedRef = useRef(false); // Track if user clicked the icon

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // Auto-open on scroll down (works every time user visits)
  useEffect(() => {
    // Don't auto-open if user has already interacted with the chat icon
    if (userInteractedRef.current) {
      return;
    }

    // If already triggered and popup is open, don't re-attach listener
    if (scrollTriggeredRef.current && isOpen) {
      return;
    }

    // Reset scroll trigger if popup was closed (but user hasn't interacted)
    if (!isOpen) {
      scrollTriggeredRef.current = false;
    }

    let lastScrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
    let scrollTimeout;

    const handleScroll = () => {
      // Don't auto-open if user has interacted, already triggered, or popup is open
      if (userInteractedRef.current || scrollTriggeredRef.current || isOpen) {
        return;
      }

      const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
      
      // Clear previous timeout
      clearTimeout(scrollTimeout);
      
      // Set a small delay to avoid opening/closing rapidly
      scrollTimeout = setTimeout(() => {
        // Check if user scrolled down (not up) and scrolled more than 150px from top
        const scrolledDown = currentScrollTop > lastScrollTop;
        const scrolledEnough = currentScrollTop >= 150;
        const scrollDelta = currentScrollTop - lastScrollTop;
        
        // Trigger if scrolled down, scrolled enough, and moved at least 50px
        if (scrolledDown && scrolledEnough && scrollDelta > 50 && !scrollTriggeredRef.current && !isOpen) {
          scrollTriggeredRef.current = true;
          setIsOpen(true);
          
          // Add welcome prompt message after a short delay
          setTimeout(() => {
            const promptMessage = {
              type: 'bot',
              text: "👋 Welcome! Ask me anything about our services or contact info!",
              timestamp: new Date()
            };
            setMessages(prev => [...prev, promptMessage]);
          }, 500);
        }
        
        // Update last scroll position
        lastScrollTop = currentScrollTop;
      }, 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [isOpen]);

  // Find matching response based on user input
  const findResponse = (userInput) => {
    const lowerInput = userInput.toLowerCase().trim();
    
    // Check each knowledge base category
    for (const [category, data] of Object.entries(knowledgeBase)) {
      for (const keyword of data.keywords) {
        if (lowerInput.includes(keyword)) {
          return { text: data.response, needsExpert: false };
        }
      }
    }

    // Check for contact/expert requests
    if (lowerInput.includes('contact') || lowerInput.includes('expert') || lowerInput.includes('speak to') || lowerInput.includes('talk to')) {
      return { text: "I'd be happy to connect you with our expert team! Let me take you to our contact page.", needsExpert: true };
    }

    // Check for greeting
    if (lowerInput.match(/^(hi|hello|hey|greetings|good morning|good afternoon|good evening)/)) {
      return { text: "Hello! 👋 How can I help you today? Ask me about our services, contact information, or anything else!", needsExpert: false };
    }

    // Check for thanks/goodbye
    if (lowerInput.match(/(thank|thanks|bye|goodbye|see you|appreciate)/)) {
      return { text: "You're welcome! 😊 If you need anything else, feel free to ask. Have a great day!", needsExpert: false };
    }

    // No match found
    return { text: fallbackResponse, needsExpert: true };
  };

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;

    const userMessage = {
      type: 'user',
      text: inputValue.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simulate bot thinking time
    setTimeout(() => {
      const response = findResponse(userMessage.text);
      
      const botMessage = {
        type: 'bot',
        text: response.text,
        timestamp: new Date(),
        needsExpert: response.needsExpert
      };

      setMessages(prev => [...prev, botMessage]);
      setIsTyping(false);

      // If expert needed, show contact button after a delay
      if (response.needsExpert) {
        setTimeout(() => {
          const contactMessage = {
            type: 'bot',
            text: 'Would you like to contact our experts now?',
            timestamp: new Date(),
            showContactButton: true
          };
          setMessages(prev => [...prev, contactMessage]);
        }, 1500);
      }
    }, 800);
  };

  const handleContactClick = () => {
    navigate('/contact');
    setIsOpen(false);
    userInteractedRef.current = true; // User interacted, disable auto-open
    scrollTriggeredRef.current = false; // Reset scroll trigger
  };

  const handleToggleChat = () => {
    setIsOpen(!isOpen);
    userInteractedRef.current = true; // User interacted, disable auto-open
    scrollTriggeredRef.current = false; // Reset scroll trigger
  };

  const handlePaymentClick = () => {
    navigate('/payment');
    setIsOpen(false);
  };

  const handleCloseChat = () => {
    setIsOpen(false);
    userInteractedRef.current = true; // User interacted, disable auto-open
    scrollTriggeredRef.current = false; // Reset scroll trigger
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="chat-assistant-container">
      {isOpen && (
        <div className="chat-popup">
          <div className="chat-popup-header">
            <div>
              <h5 className="m-0">Nexen Assistant</h5>
              <small style={{ fontSize: '0.75rem', opacity: 0.9 }}>We're here to help</small>
            </div>
            <button 
              className="close-button" 
              onClick={handleCloseChat}
              aria-label="Close chat window"
            >
              ×
            </button>
          </div>
          
          <div className="chat-messages">
            {messages.map((msg, idx) => (
              <div key={idx} className={`message ${msg.type}`}>
                <div className="message-content">
                  {msg.type === 'bot' && <div className="bot-avatar">🤖</div>}
                  <div className="message-bubble">
                    <p className="mb-0">{msg.text}</p>
                    {msg.showContactButton && (
                      <button 
                        className="contact-button-inline" 
                        onClick={handleContactClick}
                      >
                        Contact Our Experts →
                      </button>
                    )}
                  </div>
                  {msg.type === 'user' && <div className="user-avatar">👤</div>}
                </div>
                <small className="message-time">{formatTime(msg.timestamp)}</small>
              </div>
            ))}
            
            {isTyping && (
              <div className="message bot">
                <div className="message-content">
                  <div className="bot-avatar">🤖</div>
                  <div className="message-bubble typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-container">
            <input
              ref={inputRef}
              type="text"
              className="chat-input"
              placeholder="Type your message..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isTyping}
            />
            <button 
              className="send-button" 
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isTyping}
              aria-label="Send message"
            >
              <i className="bi bi-send-fill"></i>
            </button>
          </div>
        </div>
      )}
      
      {location.pathname === '/' && (
        <button
          className="payment-toggle-button"
          onClick={handlePaymentClick}
          aria-label="Make a payment"
          style={{
            position: "fixed",
            bottom: "30px",
            left: "30px",
            right: "auto",
            zIndex: 9999,
          }}
        >
          <i className="bi bi-cash-coin"></i>
          <span className="payment-pulse"></span>
        </button>
      )}

      <button 
        className="chat-toggle-button" 
        onClick={handleToggleChat}
        aria-label="Open chat assistance"
      >
        {isOpen ? (
          <i className="bi bi-x-lg"></i>
        ) : (
          <i className="bi bi-chat-dots-fill"></i>
        )}
      </button>

      <style jsx="true">{`
        .chat-assistant-container {
          position: fixed;
          bottom: 30px;
          right: 30px;
          z-index: 9999;
        }

        .chat-toggle-button {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: linear-gradient(135deg, #6B46C1 0%, #D53F8C 100%);
          border: none;
          color: white;
          font-size: 20px;
          cursor: pointer;
          box-shadow: 0 4px 15px rgba(107, 70, 193, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          animation: chatPulseSize 1.2s ease-in-out infinite;
        }

        .chat-toggle-button:hover {
          animation-play-state: paused;
          transform: scale(1.1);
          box-shadow: 0 6px 20px rgba(107, 70, 193, 0.5);
        }

        @keyframes chatPulseSize {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 4px 15px rgba(107, 70, 193, 0.3);
          }
          50% {
            transform: scale(1.25);
            box-shadow: 0 6px 25px rgba(213, 63, 140, 0.5);
          }
        }

        .payment-toggle-button {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
          border: none;
          color: white;
          font-size: 20px;
          cursor: pointer;
          box-shadow: 0 4px 15px rgba(40, 167, 69, 0.4);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          right: auto;
          animation: paymentPulse 2s ease-in-out infinite;
        }

        .payment-toggle-button:hover {
          transform: translateY(-2px) scale(1.05);
          box-shadow: 0 6px 25px rgba(40, 167, 69, 0.6);
        }

        .payment-pulse {
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          background: rgba(40, 167, 69, 0.4);
          animation: paymentRing 2s ease-out infinite;
        }

        @keyframes paymentPulse {
          0%, 100% {
            box-shadow: 0 4px 15px rgba(40, 167, 69, 0.4), 0 0 0 0 rgba(40, 167, 69, 0.7);
          }
          50% {
            box-shadow: 0 4px 15px rgba(40, 167, 69, 0.4), 0 0 0 10px rgba(40, 167, 69, 0);
          }
        }

        @keyframes paymentRing {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          100% {
            transform: scale(1.5);
            opacity: 0;
          }
        }

        .chat-popup {
          position: absolute;
          bottom: 70px;
          right: 0;
          width: 280px;
          max-width: calc(100vw - 40px);
          height: 20vh;
          min-height: 300px;
          max-height: 300px;
          background: white;
          border-radius: 20px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
          overflow: hidden;
          display: flex;
          flex-direction: column;
          transform-origin: bottom right;
          animation: popIn 0.3s ease-out;
          z-index: 9999;
        }

        .chat-popup-header {
          background: linear-gradient(90deg, #6B46C1 0%, #D53F8C 100%);
          color: white;
          padding: 10px 12px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-shrink: 0;
        }

        .chat-popup-header h5 {
          font-weight: 600;
          font-size: 0.9rem;
          margin: 0;
          line-height: 1.2;
        }

        .chat-popup-header small {
          font-size: 0.65rem;
        }

        .close-button {
          background: none;
          border: none;
          color: white;
          font-size: 20px;
          cursor: pointer;
          padding: 0 3px;
          line-height: 1;
          transition: transform 0.2s;
        }

        .close-button:hover {
          transform: scale(1.1);
        }

        .chat-messages {
          flex: 1;
          overflow-y: auto;
          padding: 10px 12px;
          background: #f8f9fa;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .chat-messages::-webkit-scrollbar {
          width: 6px;
        }

        .chat-messages::-webkit-scrollbar-track {
          background: #f1f1f1;
        }

        .chat-messages::-webkit-scrollbar-thumb {
          background: #6B46C1;
          border-radius: 3px;
        }

        .message {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .message.user {
          align-items: flex-end;
        }

        .message.bot {
          align-items: flex-start;
        }

        .message-content {
          display: flex;
          align-items: flex-end;
          gap: 6px;
          max-width: 85%;
        }

        .message.user .message-content {
          flex-direction: row-reverse;
        }

        .bot-avatar, .user-avatar {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          flex-shrink: 0;
        }

        .bot-avatar {
          background: linear-gradient(135deg, #6B46C1 0%, #D53F8C 100%);
        }

        .user-avatar {
          background: #6B46C1;
        }

        .message-bubble {
          padding: 8px 12px;
          border-radius: 12px;
          word-wrap: break-word;
          white-space: pre-wrap;
          line-height: 1.4;
          font-size: 0.85rem;
        }

        .message.bot .message-bubble {
          background: white;
          color: #333;
          border: 1px solid #e0e0e0;
          border-bottom-left-radius: 4px;
        }

        .message.user .message-bubble {
          background: linear-gradient(135deg, #6B46C1 0%, #D53F8C 100%);
          color: white;
          border-bottom-right-radius: 4px;
        }

        .message-time {
          font-size: 0.65rem;
          color: #999;
          padding: 0 6px;
        }

        .message.user .message-time {
          text-align: right;
        }

        .typing-indicator {
          display: flex;
          gap: 3px;
          padding: 8px 12px;
        }

        .typing-indicator span {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #6B46C1;
          animation: typing 1.4s infinite;
        }

        .typing-indicator span:nth-child(2) {
          animation-delay: 0.2s;
        }

        .typing-indicator span:nth-child(3) {
          animation-delay: 0.4s;
        }

        @keyframes typing {
          0%, 60%, 100% {
            transform: translateY(0);
            opacity: 0.7;
          }
          30% {
            transform: translateY(-10px);
            opacity: 1;
          }
        }

        .contact-button-inline {
          margin-top: 6px;
          padding: 6px 12px;
          background: linear-gradient(90deg, #6B46C1 0%, #D53F8C 100%);
          color: white;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          font-size: 0.75rem;
          cursor: pointer;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          width: 100%;
        }

        .contact-button-inline:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(107, 70, 193, 0.3);
        }

        .chat-input-container {
          display: flex;
          gap: 8px;
          padding: 10px 12px;
          background: white;
          border-top: 1px solid #e0e0e0;
          flex-shrink: 0;
        }

        .chat-input {
          flex: 1;
          padding: 8px 12px;
          border: 1px solid #e0e0e0;
          border-radius: 20px;
          font-size: 0.8rem;
          outline: none;
          transition: border-color 0.2s;
        }

        .chat-input:focus {
          border-color: #6B46C1;
        }

        .chat-input:disabled {
          background: #f5f5f5;
          cursor: not-allowed;
        }

        .send-button {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: linear-gradient(135deg, #6B46C1 0%, #D53F8C 100%);
          border: none;
          color: white;
          font-size: 14px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.2s, box-shadow 0.2s;
          flex-shrink: 0;
        }

        .send-button:hover:not(:disabled) {
          transform: scale(1.05);
          box-shadow: 0 4px 12px rgba(107, 70, 193, 0.3);
        }

        .send-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        @keyframes popIn {
          0% {
            transform: scale(0.9);
            opacity: 0;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        @media (max-width: 480px) {
          .chat-popup {
            width: calc(100vw - 20px);
            right: 10px;
            bottom: 70px;
            height: 20vh;
            min-height: 280px;
            max-height: 300px;
          }
          
          .chat-toggle-button {
            width: 45px;
            height: 45px;
            font-size: 18px;
            bottom: 20px;
            right: 20px;
            animation: chatPulseSize 1.2s ease-in-out infinite;
          }
          
          .payment-toggle-button {
            width: 45px;
            height: 45px;
            font-size: 18px;
            bottom: 20px;
            left: 20px;
            right: auto;
          }
          
          .chat-popup-header {
            padding: 8px 10px;
          }
          
          .chat-popup-header h5 {
            font-size: 0.85rem;
          }
          
          .chat-messages {
            padding: 8px 10px;
            gap: 8px;
          }
          
          .chat-input-container {
            padding: 8px 10px;
            gap: 6px;
          }
          
          .chat-input {
            padding: 6px 10px;
            font-size: 0.75rem;
          }
          
          .send-button {
            width: 32px;
            height: 32px;
            font-size: 12px;
          }
        }
      `}</style>
    </div>
  );
}
